// ============================================================================
// Integration API Layer
// ============================================================================

import { supabase } from '@/lib/supabase'
import type {
  IntegrationProvider,
  IntegrationCredentialSet,
  IntegrationCredential,
  IntegrationHealth,
  IntegrationOverview,
  HealthStatus,
  SyncLogEntry,
} from '@/types/integrations'

// -- Providers -----------------------------------------------------------------

export async function fetchProviders(): Promise<IntegrationProvider[]> {
  const { data, error } = await supabase
    .from('integration_providers')
    .select('*')
    .eq('is_active', true)
    .order('name')

  if (error) throw error
  return data as IntegrationProvider[]
}

// -- Credential Sets -----------------------------------------------------------

export async function fetchCredentialSets(organizationId: string): Promise<IntegrationCredentialSet[]> {
  const { data, error } = await supabase
    .from('integration_credential_sets')
    .select('id, organization_id, provider, label, username, is_active, rate_limit_rpm, created_at, updated_at')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false })

  if (error) throw error
  return data as IntegrationCredentialSet[]
}

// -- Credentials (per-location) ------------------------------------------------

export async function fetchCredentials(organizationId: string): Promise<IntegrationCredential[]> {
  const { data, error } = await supabase
    .from('integration_credentials')
    .select('*')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false })

  if (error) throw error
  return data as IntegrationCredential[]
}

export async function updateCredential(
  id: string,
  updates: Partial<Pick<IntegrationCredential, 'is_active' | 'sync_config' | 'provider_config'>>,
): Promise<void> {
  const { error } = await supabase
    .from('integration_credentials')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)

  if (error) throw error
}

export async function deleteCredential(id: string): Promise<void> {
  const { error } = await supabase
    .from('integration_credentials')
    .delete()
    .eq('id', id)

  if (error) throw error
}

// -- Health Overview -----------------------------------------------------------

export async function fetchIntegrationHealth(organizationId: string): Promise<IntegrationHealth[]> {
  const { data, error } = await supabase
    .from('my_integration_health')
    .select('*')
    .eq('organization_id', organizationId)

  if (error) throw error
  return data as IntegrationHealth[]
}

// -- Sync Log ------------------------------------------------------------------

export async function fetchSyncLog(
  organizationId: string,
  options?: {
    credentialId?: string
    credentialIds?: string[]
    result?: string
    limit?: number
    offset?: number
  },
): Promise<SyncLogEntry[]> {
  let query = supabase
    .from('integration_sync_log')
    .select('*')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false })
    .limit(options?.limit ?? 50)

  if (options?.credentialIds?.length) {
    query = query.in('credential_id', options.credentialIds)
  } else if (options?.credentialId) {
    query = query.eq('credential_id', options.credentialId)
  }
  if (options?.result) {
    query = query.eq('result', options.result)
  }
  if (options?.offset) {
    query = query.range(options.offset, options.offset + (options.limit ?? 50) - 1)
  }

  const { data, error } = await query

  if (error) throw error
  return data as SyncLogEntry[]
}

// -- Integration Overview (grouped by credential set) -------------------------

function computeHealthStatus(cred: {
  is_active: boolean
  consecutive_failures: number
  last_successful_sync: string | null
}): HealthStatus {
  if (!cred.is_active) return 'disabled'
  if (cred.consecutive_failures >= 10) return 'circuit_broken'
  if (cred.consecutive_failures >= 3) return 'degraded'
  if (!cred.last_successful_sync) return 'never_synced'
  const minutesSinceSync = (Date.now() - new Date(cred.last_successful_sync).getTime()) / 60000
  if (minutesSinceSync > 15) return 'stale'
  return 'healthy'
}

export async function fetchIntegrationOverview(organizationId: string): Promise<IntegrationOverview[]> {
  const { data, error } = await supabase
    .from('integration_credential_sets')
    .select(`
      id, provider, label, provider_config, is_active, created_at,
      integration_providers!provider (code, name, provider_type, capabilities, is_active, credential_model, website_url, docs_url),
      integration_credentials!credential_set_id (
        id, location_id, is_active, last_successful_sync, consecutive_failures, last_error, last_error_at,
        locations!location_id (name, location_code)
      )
    `)
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: true })

  if (error) throw error

  // Enrich each credential with computed health_status
  return (data ?? []).map((set) => ({
    ...set,
    integration_credentials: (set.integration_credentials ?? []).map((cred: Record<string, unknown>) => ({
      ...cred,
      health_status: computeHealthStatus(cred as {
        is_active: boolean
        consecutive_failures: number
        last_successful_sync: string | null
      }),
    })),
  })) as IntegrationOverview[]
}

export async function updateCredentialSet(
  id: string,
  updates: { is_active: boolean },
): Promise<void> {
  const { error } = await supabase
    .from('integration_credential_sets')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)

  if (error) throw error
}

export async function bulkUpdateCredentials(
  credentialSetId: string,
  updates: { is_active: boolean },
): Promise<void> {
  const { error } = await supabase
    .from('integration_credentials')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('credential_set_id', credentialSetId)

  if (error) throw error
}

export async function removeIntegration(
  credentialSetId: string,
  credentialModel: 'platform' | 'per_org' | 'per_location',
): Promise<void> {
  // Always delete all credentials for this set
  const { error: credErr } = await supabase
    .from('integration_credentials')
    .delete()
    .eq('credential_set_id', credentialSetId)

  if (credErr) throw credErr

  if (credentialModel === 'per_org') {
    // Delete the credential set entirely (cascade cleans vault)
    const { error: setErr } = await supabase
      .from('integration_credential_sets')
      .delete()
      .eq('id', credentialSetId)

    if (setErr) throw setErr
  } else {
    // Platform: just deactivate the set (creds managed by PlaneMAX)
    const { error: setErr } = await supabase
      .from('integration_credential_sets')
      .update({ is_active: false, updated_at: new Date().toISOString() })
      .eq('id', credentialSetId)

    if (setErr) throw setErr
  }
}

// -- Edge Function Calls -------------------------------------------------------

async function callSetupFunction(body: Record<string, unknown>): Promise<Record<string, unknown>> {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session) throw new Error('Not authenticated')

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
  const response = await fetch(`${supabaseUrl}/functions/v1/integration-setup`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: JSON.stringify(body),
  })

  const result = await response.json()
  if (!response.ok) {
    throw new Error(result.error ?? 'Setup request failed')
  }
  return result
}

export async function testCredentials(
  organizationId: string,
  provider: string,
  username: string,
  password: string,
): Promise<{ success: boolean; message: string }> {
  const result = await callSetupFunction({
    action: 'test_credentials',
    organization_id: organizationId,
    provider,
    username,
    password,
  })
  return result as { success: boolean; message: string }
}

export async function createCredentialSet(
  organizationId: string,
  provider: string,
  label: string,
  username: string,
  password: string,
): Promise<{ credential_set_id: string }> {
  const result = await callSetupFunction({
    action: 'create_credential_set',
    organization_id: organizationId,
    provider,
    label,
    username,
    password,
  })
  return result as { credential_set_id: string }
}

export async function mapLocation(
  organizationId: string,
  credentialSetId: string,
  locationId: string,
  providerConfig?: Record<string, unknown>,
  syncConfig?: Record<string, unknown>,
): Promise<{ credential_id: string }> {
  const result = await callSetupFunction({
    action: 'map_location',
    organization_id: organizationId,
    credential_set_id: credentialSetId,
    location_id: locationId,
    provider_config: providerConfig,
    sync_config: syncConfig,
  })
  return result as { credential_id: string }
}

export async function triggerInitialSync(
  _organizationId: string,
  credentialId: string,
): Promise<{ status: string }> {
  // Reset credential state so Laravel picks it up on the next poll cycle (≤60s)
  const { error } = await supabase
    .from('integration_credentials')
    .update({
      last_successful_sync: null,
      consecutive_failures: 0,
      last_error: null,
    })
    .eq('id', credentialId)

  if (error) throw new Error(error.message)
  return { status: 'queued' }
}

export async function rotateCredentials(
  organizationId: string,
  credentialSetId: string,
  newPassword: string,
): Promise<{ success: boolean; message: string }> {
  const result = await callSetupFunction({
    action: 'rotate_credentials',
    organization_id: organizationId,
    credential_set_id: credentialSetId,
    new_password: newPassword,
  })
  return result as { success: boolean; message: string }
}

export async function triggerManualSync(credentialId: string): Promise<{ status: string }> {
  // Reset credential state so Laravel picks it up on the next poll cycle (≤60s)
  const { error } = await supabase
    .from('integration_credentials')
    .update({
      last_successful_sync: null,
      consecutive_failures: 0,
      last_error: null,
    })
    .eq('id', credentialId)

  if (error) throw new Error(error.message)
  return { status: 'queued' }
}
